import React from 'react';
import { Shuffle, Volume2, VolumeX, Clock, Palette } from 'lucide-react';
import { PRESET_PALETTES } from '../../utils/colors';

interface WheelControlsProps {
  duration: number;
  onDurationChange: (duration: number) => void;
  onShuffle: () => void;
  soundEnabled: boolean;
  onToggleSound: () => void;
  paletteId: string;
  onPaletteChange: (paletteId: string) => void;
  isSpinning: boolean;
  totalEntries: number;
}

export const WheelControls: React.FC<WheelControlsProps> = ({
  duration,
  onDurationChange,
  onShuffle,
  soundEnabled,
  onToggleSound,
  paletteId,
  onPaletteChange,
  isSpinning,
  totalEntries,
}) => {
  const durations = [3, 5, 8, 10];

  return (
    <div
      className="glass-panel"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        padding: '0.5rem 0.75rem',
        width: '100%',
        maxWidth: '560px',
        margin: '0 auto',
      }}
    >
      {/* Duration Selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <Clock size={15} color="var(--text-muted)" style={{ flexShrink: 0 }} />
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>Duration:</span>
        <div style={{ display: 'flex', gap: '3px', background: 'var(--bg-surface-elevated)', padding: '2px', borderRadius: '8px' }}>
          {durations.map((d) => (
            <button
              key={d}
              disabled={isSpinning}
              onClick={() => onDurationChange(d)}
              style={{
                padding: '3px 8px',
                fontSize: '0.78rem',
                fontWeight: 600,
                borderRadius: '6px',
                background: duration === d ? 'var(--accent-primary)' : 'transparent',
                color: duration === d ? '#ffffff' : 'var(--text-secondary)',
              }}
            >
              {d}s
            </button>
          ))}
        </div>
      </div>

      <div style={{ width: '1px', height: '18px', background: 'var(--border-subtle)' }} />

      {/* Palette Selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <Palette size={15} color="var(--text-muted)" style={{ flexShrink: 0 }} />
        <select
          value={paletteId}
          disabled={isSpinning}
          onChange={(e) => onPaletteChange(e.target.value)}
          style={{
            fontSize: '0.78rem',
            padding: '3px 6px',
            width: 'auto',
            borderRadius: '8px',
            background: 'var(--bg-surface-elevated)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          {PRESET_PALETTES.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <div style={{ width: '1px', height: '18px', background: 'var(--border-subtle)' }} />

      {/* Quick Action Buttons */}
      <div style={{ display: 'flex', gap: '6px' }}>
        <button
          className="btn-icon"
          onClick={onShuffle}
          disabled={isSpinning || totalEntries <= 1}
          title="Shuffle items"
          aria-label="Shuffle items"
          style={{ width: '30px', height: '30px' }}
        >
          <Shuffle size={14} />
        </button>

        <button
          className="btn-icon"
          onClick={onToggleSound}
          title={soundEnabled ? 'Mute Sound' : 'Enable Sound'}
          aria-label={soundEnabled ? 'Mute Sound' : 'Enable Sound'}
          style={{ width: '30px', height: '30px' }}
        >
          {soundEnabled ? <Volume2 size={14} color="var(--accent-primary)" /> : <VolumeX size={14} />}
        </button>
      </div>
    </div>
  );
};
